import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RbacProvider } from './context/RbacContext';
import Layout from './components/layout/Layout';
import RequireRole from './components/common/RequireRole';

// Auth
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import AcceptInvite from './pages/auth/AcceptInvite';
import RequestCareGate from './pages/auth/RequestCareGate';

// Public marketing site (unauthed)
import MarketingHome     from './pages/marketing/Home';
import MarketingAbout    from './pages/marketing/About';
import MarketingFeatures from './pages/marketing/Features';
import MarketingContact  from './pages/marketing/Contact';
import MarketingPricing  from './pages/marketing/Pricing';
import Privacy           from './pages/marketing/Privacy';
import Terms             from './pages/marketing/Terms';
import NotFound          from './pages/marketing/NotFound';

// Overview
import Dashboard from './pages/dashboard/Dashboard';
import Analytics from './pages/analytics/Analytics';
import RiskDashboard from './pages/analytics/RiskDashboard';

// Care Operations
import Referrals from './pages/referrals/Referrals';
import CareRequests from './pages/requests/CareRequests';
import Dispatch from './pages/nurses/Dispatch';
import Branches from './pages/admin/Branches';
import Visits from './pages/visits/Visits';
import VisitCalendar from './pages/visits/VisitCalendar';
import RecurringVisits from './pages/visits/RecurringVisits';
import CarePlans from './pages/care-plans/CarePlans';
import Episodes from './pages/episodes/Episodes';

// Clinical
import ClinicalReports from './pages/clinical/ClinicalReports';
import Prescriptions from './pages/clinical/Prescriptions';
import RemoteMonitoring from './pages/clinical/RemoteMonitoring';
import PatientWorkspace from './pages/clinical/PatientWorkspace';
import OasisList from './pages/clinical/oasis/OasisList';
import OasisAssessment from './pages/clinical/oasis/OasisAssessment';

// Finance & Admin
import Billing from './pages/billing/Billing';
import RevenueCycle from './pages/billing/RevenueCycle';
import BillingDashboard from './pages/billing/BillingDashboard';
import InvoiceDetail from './pages/billing/InvoiceDetail';
import RateCards from './pages/billing/RateCards';
import InsuranceManagement from './pages/billing/InsuranceManagement';
import BillingConfig from './pages/billing/BillingConfig';
import Compliance from './pages/compliance/Compliance';
import RbacAdmin from './pages/rbac/RbacAdmin';
import AccessManagement from './pages/rbac/AccessManagement';

// Identity Platform (ss* Parties Module)
import SSParties from './pages/parties-module/SSParties';
import SSNurses from './pages/workforce-module/SSNurses';
import SSDoctors from './pages/workforce-module/SSDoctors';
import SSUsers from './pages/parties-module/SSUsers';
import SSRoles from './pages/parties-module/SSRoles';
import SSPermissions from './pages/parties-module/SSPermissions';
import SSCompanies from './pages/parties-module/SSCompanies';
import SSOrgUnits from './pages/parties-module/SSOrgUnits';
import SSRelationships from './pages/parties-module/SSRelationships';
import SSAudit from './pages/parties-module/SSAudit';
import SSSecurityCatalog from './pages/parties-module/SSSecurityCatalog';
import SSNavigation from './pages/parties-module/SSNavigation';
import SSLookupConfig from './pages/parties-module/SSLookupConfig';

// Settings — Website (public marketing site editor)
import WebsiteSettings from './pages/settings/website/WebsiteSettings';
import MediaManager    from './pages/settings/website/MediaManager';

// My Profile (all roles)
import Profile from './pages/profile/Profile';

// Access control
import NoAccess from './pages/NoAccess';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#8b7681', fontFamily: 'Inter,sans-serif', fontSize: 14, flexDirection: 'column', gap: 12,
    }}>
      <div style={{ fontSize: 32 }}>⚕️</div>
      Loading Benevita…
    </div>
  );
  return user ? children : <Navigate to="/login" replace />;
};

// Helper — wraps the element in a RequireRole guard tied to a permission key
const P = (permission, element) => (
  <RequireRole permission={permission}>{element}</RequireRole>
);

// This host (app.benevitahomecare.com) is the application portal. Marketing
// lives on the separate site (www.benevitahomecare.com), so unauthed visitors
// at "/" go straight to login; authed visitors continue to the dashboard.
const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

function AppRoutes() {
  return (
    <Routes>
      {/* ── Public marketing site (no auth required) ────────────────────── */}
      <Route path="/home"     element={<MarketingHome />} />
      <Route path="/about"    element={<MarketingAbout />} />
      <Route path="/features" element={<MarketingFeatures />} />
      <Route path="/contact"  element={<MarketingContact />} />
      <Route path="/pricing"  element={<MarketingPricing />} />
      <Route path="/privacy"  element={<Privacy />} />
      <Route path="/terms"    element={<Terms />} />
      <Route path="/404"      element={<NotFound />} />

      {/* ── Auth ────────────────────────────────────────────────────────── */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/request-care" element={<RequestCareGate />} />

      {/* ── Root: route by auth state ───────────────────────────────────── */}
      <Route path="/" element={<RootRedirect />} />

      {/* ── Protected app ───────────────────────────────────────────────── */}
      <Route path="/*" element={
        <ProtectedRoute>
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* No-access page (still behind auth) */}
              <Route path="/no-access" element={<NoAccess />} />

              {/* Overview */}
              <Route path="/dashboard" element={P('page.dashboard', <Dashboard />)} />
              <Route path="/analytics" element={P('page.analytics', <Analytics />)} />
              <Route path="/risk-dashboard" element={P('page.analytics', <RiskDashboard />)} />

              {/* Parties — redirect legacy routes to the new SS Party Explorer */}
              <Route path="/parties"       element={<Navigate to="/ss/parties" replace />} />
              <Route path="/patients"      element={<Navigate to="/ss/parties?role_code=PATIENT" replace />} />
              <Route path="/nurses"        element={<Navigate to="/ss/nurses"  replace />} />
              <Route path="/doctors"       element={<Navigate to="/ss/doctors" replace />} />
              <Route path="/organizations" element={<Navigate to="/ss/companies" replace />} />

              {/* Care Operations */}
              <Route path="/referrals"     element={P('page.referrals',   <Referrals />)} />
              <Route path="/care-requests" element={P('page.requests',    <CareRequests />)} />
              <Route path="/dispatch"      element={P('page.dispatch',    <Dispatch />)} />
              <Route path="/branches"      element={P('page.dispatch',    <Branches />)} />
              <Route path="/visits"            element={P('page.visits',          <Visits />)} />
              <Route path="/visit-calendar"   element={P('page.visits',          <VisitCalendar />)} />
              <Route path="/recurring-visits" element={P('page.visits',          <RecurringVisits />)} />
              <Route path="/care-plans"    element={P('page.care_plans',  <CarePlans />)} />
              <Route path="/episodes"      element={P('page.episodes',    <Episodes />)} />

              {/* Clinical */}
              <Route path="/clinical"      element={P('page.clinical',      <ClinicalReports />)} />
              <Route path="/prescriptions" element={P('page.prescriptions', <Prescriptions />)} />
              <Route path="/remote-obs"    element={P('page.rpm',           <RemoteMonitoring />)} />
              <Route path="/patient-360/:partyId" element={P('page.patients', <PatientWorkspace />)} />
              <Route path="/oasis"           element={P('page.clinical', <OasisList />)} />
              <Route path="/oasis/:id"       element={P('page.clinical', <OasisAssessment />)} />

              {/* Finance & Admin */}
              <Route path="/billing"                element={P('page.billing',       <BillingDashboard />)} />
              <Route path="/billing/new"            element={P('page.billing_admin', <Billing />)} />
              <Route path="/billing/:billing_id"    element={P('page.billing',       <InvoiceDetail />)} />
              <Route path="/billing/rate-cards"     element={P('page.billing_admin', <RateCards />)} />
              <Route path="/billing/insurance"      element={P('page.billing_admin', <InsuranceManagement />)} />
              <Route path="/billing/config"         element={P('page.billing_admin', <BillingConfig />)} />
              <Route path="/revenue-cycle"          element={P('page.billing_admin', <RevenueCycle />)} />
              <Route path="/compliance" element={P('page.audit',   <Compliance />)} />
              <Route path="/rbac"       element={P('page.rbac',    <RbacAdmin />)} />
              <Route path="/access"     element={P('page.rbac',    <AccessManagement />)} />

              {/* Identity Platform — ss* Parties Module */}
              <Route path="/ss/parties"       element={P('page.ss_parties',       <SSParties />)} />
              <Route path="/ss/nurses"        element={P('page.ss_nurses',        <SSNurses />)} />
              <Route path="/ss/doctors"       element={P('page.ss_doctors',       <SSDoctors />)} />
              <Route path="/ss/users"         element={P('page.ss_users',         <SSUsers />)} />
              <Route path="/ss/roles"         element={P('page.ss_roles',         <SSRoles />)} />
              <Route path="/ss/permissions"   element={P('page.ss_permissions',   <SSPermissions />)} />
              <Route path="/ss/companies"     element={P('page.ss_companies',     <SSCompanies />)} />
              <Route path="/ss/org-units"     element={P('page.ss_org_units',     <SSOrgUnits />)} />
              <Route path="/ss/relationships" element={P('page.ss_relationships', <SSRelationships />)} />
              <Route path="/ss/audit"            element={P('page.ss_audit',            <SSAudit />)} />
              <Route path="/ss/security-catalog" element={P('page.ss_security_catalog', <SSSecurityCatalog />)} />
              <Route path="/ss/navigation"       element={P('page.ss_navigation',       <SSNavigation />)} />
              <Route path="/ss/lookup-config"    element={P('page.ss_lookup_config',    <SSLookupConfig />)} />

              {/* Settings */}
              <Route path="/settings/website"    element={P('marketing.config.read',    <WebsiteSettings />)} />
              <Route path="/settings/media"      element={P('marketing.config.write',   <MediaManager />)} />
              <Route path="/profile"             element={P('page.profile',              <Profile />)} />

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Layout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RbacProvider>
          <AppRoutes />
        </RbacProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
